import {Constants} from '@/utils/constants/commonConstants';
import {
  LEGACY_PERFECT_STORE_TARGET_TYPE_VALUES,
  PERFECT_STORE_TARGET_TYPE_VALUES
} from '@/utils/constants/perfectStoreConstants';
import {ROUTES} from '@/utils/constants/routesConstants';
import {
  perfectStoreInitialFormData,
  perfectStoreInitialState
} from '@/store/slices/perfectStoreSlice';
import {downloadTemplateFile} from '@/services/downloadTemplateFile';
import {getPerfectStoreReportDownload} from '@/services/perfectStore';
import {downloadCSVFiles, getDateWithTimeString} from '@/helpers/shadcn/utils';
import {logger} from '@/helpers/logger';

export const PERFECT_STORE_STATUS = {
  Draft: Constants.Draft,
  Active: 'Active',
  InProgress: Constants.InProgress,
  Published: Constants.Published,
  Completed: Constants.Completed,
  Expired: 'Expired'
};

export const matchesPerfectStoreStatus = (status, expected) =>
  (status ?? '').trim().toLowerCase() ===
  String(expected ?? '')
    .trim()
    .toLowerCase();

const matchesAnyStatus = (status, expectedList) =>
  expectedList.some((expected) => matchesPerfectStoreStatus(status, expected));

/** Panel EDIT hidden for Completed and Expired. */
export const isPerfectStoreEditable = (status) =>
  !matchesPerfectStoreStatus(status, PERFECT_STORE_STATUS.Completed) &&
  !matchesPerfectStoreStatus(status, PERFECT_STORE_STATUS.Expired);

/** Save as Draft only for empty status or Draft. */
export const isPerfectStoreSaveDraftEnabled = (status) => {
  if (status == null || String(status).trim() === '') return true;
  return matchesPerfectStoreStatus(status, PERFECT_STORE_STATUS.Draft);
};

export const isPerfectStoreCompletedOrExpired = (status) =>
  matchesPerfectStoreStatus(status, PERFECT_STORE_STATUS.Completed) ||
  matchesPerfectStoreStatus(status, PERFECT_STORE_STATUS.Expired);

/** Footer hidden by default for Active / Published / In-Progress until Edit. */
export const isPerfectStoreFooterHiddenByDefault = (status) =>
  matchesAnyStatus(status, [
    PERFECT_STORE_STATUS.Active,
    PERFECT_STORE_STATUS.Published,
    PERFECT_STORE_STATUS.InProgress
  ]);

const DUPLICATE_ENABLED = [
  PERFECT_STORE_STATUS.Active,
  PERFECT_STORE_STATUS.InProgress,
  PERFECT_STORE_STATUS.Published,
  PERFECT_STORE_STATUS.Completed
];

const DOWNLOAD_ENABLED = [
  PERFECT_STORE_STATUS.InProgress,
  PERFECT_STORE_STATUS.Completed
];

/** AC9: Duplicate is enabled for Active, In-Progress, Published, and Completed. */
export const isPerfectStoreDuplicateOrDownloadEnabled = (status) =>
  matchesAnyStatus(status, DUPLICATE_ENABLED);

export const isPerfectStoreDuplicateEnabled = (status) =>
  isPerfectStoreDuplicateOrDownloadEnabled(status);

/** Download is enabled only for In-Progress and Completed. */
export const isPerfectStoreDownloadEnabled = (status) =>
  matchesAnyStatus(status, DOWNLOAD_ENABLED);

const IDENTITY_FIELDS_LOCKED_STATUSES = [
  PERFECT_STORE_STATUS.Draft,
  PERFECT_STORE_STATUS.InProgress,
  PERFECT_STORE_STATUS.Published
];

/** Target Type and Name stay locked once a saved Draft, In-Progress, or Published status exists. */
export const isPerfectStoreIdentityFieldsLocked = (status) =>
  matchesAnyStatus(status, IDENTITY_FIELDS_LOCKED_STATUSES);

const ACTIVE_START_DATE_LOCKED_STATUSES = [
  PERFECT_STORE_STATUS.Draft,
  PERFECT_STORE_STATUS.InProgress
];

/** Active Start Date is locked for saved Draft and In-Progress targets. Published stays editable. */
export const isPerfectStoreActiveStartDateLocked = (status) =>
  matchesAnyStatus(status, ACTIVE_START_DATE_LOCKED_STATUSES);

const UPDATE_STORE_VISITS_TAB_ENABLED_STATUSES = [
  PERFECT_STORE_STATUS.Active,
  PERFECT_STORE_STATUS.InProgress,
  PERFECT_STORE_STATUS.Completed
];

/** Update Store Visits tab enabled only for Active/In-Progress/Completed. */
export const isPerfectStoreUpdateStoreVisitsTabEnabled = (status) =>
  matchesAnyStatus(status, UPDATE_STORE_VISITS_TAB_ENABLED_STATUSES);

/** Normalize API status casing (e.g. In-progress → In-Progress). */
export const normalizePerfectStoreStatus = (status) => {
  if (status == null || String(status).trim() === '') {
    return PERFECT_STORE_STATUS.Draft;
  }
  const normalized = String(status).trim().toLowerCase();
  const known = Object.values(PERFECT_STORE_STATUS);
  const match = known.find((value) => value.toLowerCase() === normalized);
  return match || status;
};

const TARGET_TYPE_API_TO_UI = {
  [LEGACY_PERFECT_STORE_TARGET_TYPE_VALUES.FIRST_POSITION]:
    PERFECT_STORE_TARGET_TYPE_VALUES.FIRST_POSITION,
  [LEGACY_PERFECT_STORE_TARGET_TYPE_VALUES.RACKS_AND_COOLERS]:
    PERFECT_STORE_TARGET_TYPE_VALUES.RACKS_AND_COOLERS,
  [LEGACY_PERFECT_STORE_TARGET_TYPE_VALUES.STORE_EXECUTION]:
    PERFECT_STORE_TARGET_TYPE_VALUES.STORE_EXECUTION,
  [LEGACY_PERFECT_STORE_TARGET_TYPE_VALUES.MUST_HAVE_PRODUCTS]:
    PERFECT_STORE_TARGET_TYPE_VALUES.MUST_HAVE_PRODUCTS,
  [LEGACY_PERFECT_STORE_TARGET_TYPE_VALUES.STORE_EXECUTION_PEPIRIS]:
    PERFECT_STORE_TARGET_TYPE_VALUES.STORE_EXECUTION_PEPIRIS
};

export const normalizePerfectStoreTargetType = (targetType) => {
  if (!targetType) return '';
  return TARGET_TYPE_API_TO_UI[targetType] || targetType;
};

/**
 * Download Perfect Store CSV template via shared templateDownload API.
 * `type` is the Perfect Store target type (e.g. "First Position Target").
 */
export const downloadPerfectStoreTemplate = async (targetType) => {
  const type = normalizePerfectStoreTargetType(targetType);
  if (!type) return false;

  const response = await downloadTemplateFile({type});
  if (!response) return false;

  const fileName = `${type.replace(/[-\s]/g, '')}_${getDateWithTimeString()}`;
  downloadCSVFiles(response, fileName);
  return true;
};

// Strips characters that aren't filename-safe from the target name.
const sanitizeForFileName = (value = '') =>
  String(value).replace(/[^a-zA-Z0-9]/g, '');

/**
 * Shared Perfect Store report download flow (list page and preview/review page)
 * against the `{success, totalRecords, blobUrl, sasToken}` API contract.
 * `downloadFromBlobUrl` comes from useAzureBlob, `onToast(message, type)` surfaces feedback.
 * `targetName` is interpolated into the downloaded file name, e.g.
 * `PerfectStoreTargets_<Name>Report_<timestamp>`.
 */
export const downloadPerfectStoreReport = async (
  targetId,
  {downloadFromBlobUrl, onToast, targetName} = {}
) => {
  if (!targetId) return;

  try {
    const response = await getPerfectStoreReportDownload(targetId);

    if (!response?.success) {
      onToast?.(
        response?.message || 'Failed to download perfect store',
        'error'
      );
      return;
    }

    if (response?.totalRecords === 0) {
      onToast?.('No records found for this perfect store', 'info');
      return;
    }

    if (!response?.blobUrl || !response?.sasToken) {
      onToast?.('Report data not available', 'error');
      return;
    }

    const csvText = await downloadFromBlobUrl(
      response.blobUrl,
      response.sasToken
    );
    if (csvText) {
      const sanitizedName = sanitizeForFileName(targetName);
      const fileName = `PerfectStoreTargets_${sanitizedName}Report_${getDateWithTimeString()}`;
      downloadCSVFiles(csvText, fileName);
    } else {
      onToast?.('Failed to download report data', 'error');
    }
  } catch (error) {
    logger.error('Error downloading perfect store:', error);
    onToast?.('Failed to download perfect store', 'error');
  }
};

/** Format ISO timestamp as YYYY-MM-DD for Preview header. */
export const formatPerfectStoreLastUpdated = (isoDate) => {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Clone form + meta for Duplicate → Setup without name / ids.
 */
export const buildPerfectStoreDuplicateFormData = (formData = {}) => {
  const cloned = JSON.parse(JSON.stringify(formData || {}));
  return {
    ...perfectStoreInitialFormData,
    ...cloned,
    name: '',
    nameValid: undefined,
    status: PERFECT_STORE_STATUS.Draft
  };
};

export const buildPerfectStoreDuplicateMeta = () => ({
  id: null,
  uuid: null,
  status: null,
  updatedAt: null,
  createdAt: null
});

export const getPerfectStoreDuplicateInitialState = (formData) => ({
  ...perfectStoreInitialState,
  formData: buildPerfectStoreDuplicateFormData(formData),
  ...buildPerfectStoreDuplicateMeta()
});

/**
 * Wizard step 3: return to Preview detail when editing an existing target,
 * otherwise creation Review.
 */
export const getPerfectStoreWizardReviewRoute = ({isEdit, id} = {}) => {
  if (isEdit && id != null && String(id).trim() !== '') {
    return ROUTES.PERFECT_STORE_DETAIL.replace(':id', encodeURIComponent(id));
  }
  return ROUTES.PERFECT_STORE_REVIEW;
};
